import * as React from 'react';
import { connect, useSelector } from 'react-redux';
import { IVisitedOffersContext, withVisitedOffersContext } from '@cian/frontend-visited-offers-widget';
import { compose } from 'ramda';

import { IApplicationState } from '../../types/redux';

import { OffersBodyContainer } from './OffersBodyContainer';
import { SimilarNewbuildings } from './SimilarNewbuildings';
import { OffersLayout } from '../../components/OffersLayout';
import { SearchPreloader } from '../../components/SearchPreloader';
import {
  getIsNewbuildingPromoSnippetDisplayed,
  getIsNewbuildingPromoSnippetEnabled,
  getNewbuildingPromoSnippet,
} from '../../selectors/newbuildingPromoSnippet';
import { getUser } from '../../selectors/user';
import { TProduct } from '../../types/analytics';
import { INewbuilding, INewbuildingPromoSnippet } from '../../types/newbuilding';
import { PromoSnippetContainer } from '../PromoSnippetContainer';
import { trackPageViewCollect } from './tracking';
import { EmptySearchResult } from './EmptySearchResult';

interface IOffersStoreProps {
  newbuildings: INewbuilding[];
  newbuildingPromoSnippet?: INewbuildingPromoSnippet | null;
  isPreloaderVisible: boolean;
  isNewbuildingPromoSnippetEnabled: boolean;
  isNewbuildingPromoSnippetDisplayed: boolean;
  hasContent: boolean;
}

interface IOwnProps {
  isOfferVisited(offerId: number): boolean;
  isLoaded: boolean;
}

type TOffersProps = IOffersStoreProps & IOwnProps;

export const Offers: React.FunctionComponent<TOffersProps> = props => {
  const {
    newbuildings = [],
    isPreloaderVisible,
    newbuildingPromoSnippet,
    isNewbuildingPromoSnippetEnabled,
    isNewbuildingPromoSnippetDisplayed,
    isLoaded: isVisitedOffersLoaded,
    isOfferVisited,
  } = props;

  const user = useSelector(getUser);

  const isSnippetDisplayed = isNewbuildingPromoSnippetEnabled && isNewbuildingPromoSnippetDisplayed;

  React.useEffect(() => {
    if (isVisitedOffersLoaded) {
      const products: TProduct[] = [];

      if (newbuildingPromoSnippet && isSnippetDisplayed) {
        products.push({
          id: newbuildingPromoSnippet.newbuilding.id,
          offerType: 'JK' as const,
          position: 1,
          extra: isOfferVisited(newbuildingPromoSnippet.newbuilding.id) ? { is_viewes: true } : undefined,
        });
      }

      const result = products.concat(
        newbuildings.map(({ id }, index) => ({
          id,
          offerType: 'JK' as const,
          position: products.length + index + 1,
          extra: isOfferVisited(id) ? { is_viewes: true } : undefined,
        })),
      );

      trackPageViewCollect({ user, products: result });
    }
  }, [isOfferVisited, isSnippetDisplayed, isVisitedOffersLoaded, newbuildingPromoSnippet, newbuildings, user]);

  return (
    <>
      {!!newbuildings.length && (
        <OffersLayout>
          <PromoSnippetContainer />
          <OffersBodyContainer />
        </OffersLayout>
      )}
      {!newbuildings.length && <EmptySearchResult />}
      <SimilarNewbuildings hasNewbuildings={!!newbuildings.length} />
      {isPreloaderVisible && <SearchPreloader />}
    </>
  );
};

export function mapStateToProps(state: IApplicationState): IOffersStoreProps {
  return {
    newbuildings: state.offersData.newbuildings,
    hasContent: state.offersData.newbuildings.length > 0 || state.offersData.similarNewbuildings.length > 0,
    isPreloaderVisible: state.preloader.isVisible,
    newbuildingPromoSnippet: getNewbuildingPromoSnippet(state),
    isNewbuildingPromoSnippetEnabled: getIsNewbuildingPromoSnippetEnabled(state),
    isNewbuildingPromoSnippetDisplayed: getIsNewbuildingPromoSnippetDisplayed(state),
  };
}

export function mapContextToProps({ isOfferVisited, isLoaded }: IVisitedOffersContext) {
  return {
    isOfferVisited,
    isLoaded,
  };
}

export const OffersContainer = compose(withVisitedOffersContext(mapContextToProps), connect(mapStateToProps))(Offers);
